export const ACCOUNT_TYPES = [
  "meta_prepaid",
  "meta_card",
  "meta_postpaid",
  "google_estimated",
  "google_invoice",
  "unknown",
];

export const PLATFORMS = ["meta", "google"];

export const STATUS_LABELS = {
  critical: "Ação agora",
  reserve: "Em reserva",
  request_balance: "Solicitar saldo",
  watch: "Acompanhar",
  ok: "Ok",
  card_watch: "Cartão/pós-pago",
  unknown: "Sem dados",
  sync_error: "Erro de sync",
};

export const STATUS_ORDER = [
  "critical",
  "sync_error",
  "reserve",
  "request_balance",
  "watch",
  "card_watch",
  "unknown",
  "ok",
];

export function isCardAccount(accountType) {
  return accountType === "meta_card" || accountType === "meta_postpaid";
}

export function isBalanceAccount(accountType) {
  return accountType === "meta_prepaid" || accountType === "google_estimated" || accountType === "google_invoice";
}

export function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function roundMoney(value) {
  return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
}

export function calculateReserve({ reserveDailySpend = 0, reserveDays = 0 }) {
  return roundMoney(Math.max(0, toNumber(reserveDailySpend)) * Math.max(0, toNumber(reserveDays)));
}

export function calculateBalanceProjection({ availableBalance, plannedDailySpend, reserveDailySpend, reserveDays }) {
  const reserveAmount = calculateReserve({ reserveDailySpend, reserveDays });
  const balance = availableBalance == null ? null : roundMoney(availableBalance);
  const operationalBalance = balance == null ? null : roundMoney(balance - reserveAmount);
  const daily = Math.max(0, toNumber(plannedDailySpend));
  const daysUntilReserve =
    operationalBalance == null || daily <= 0 ? null : roundMoney(operationalBalance / daily);

  return {
    reserveAmount,
    operationalBalance,
    daysUntilReserve,
  };
}

export function calculateSpendVariance({ spendToday = 0, plannedDailySpend = 0 }) {
  const planned = Math.max(0, toNumber(plannedDailySpend));
  const spent = Math.max(0, toNumber(spendToday));
  if (planned <= 0) {
    return {
      ratio: null,
      overPlanned: false,
      underPlanned: false,
    };
  }

  const ratio = spent / planned;
  return {
    ratio: roundMoney(ratio),
    overPlanned: ratio >= 1.15,
    underPlanned: ratio <= 0.45,
  };
}

export function classifyAccount(account, latestSnapshot = {}) {
  if (latestSnapshot?.errorCode || latestSnapshot?.error_code || account?.last_error) {
    return "sync_error";
  }

  const accountType = account?.accountType ?? account?.account_type ?? "unknown";
  const plannedDailySpend = toNumber(account?.plannedDailySpend ?? account?.planned_daily_spend);
  const reserveDailySpend = toNumber(account?.reserveDailySpend ?? account?.reserve_daily_spend);
  const reserveDays = toNumber(account?.reserveDays ?? account?.reserve_days);
  const alertBeforeReserveDays = toNumber(
    account?.alertBeforeReserveDays ?? account?.alert_before_reserve_days,
    5
  );
  const availableBalance =
    latestSnapshot?.availableBalance ??
    latestSnapshot?.available_balance ??
    latestSnapshot?.estimatedBalance ??
    latestSnapshot?.estimated_balance ??
    null;
  const spendToday = latestSnapshot?.spendToday ?? latestSnapshot?.spend_today ?? 0;
  const variance = calculateSpendVariance({ spendToday, plannedDailySpend });

  if (isCardAccount(accountType)) {
    return variance.overPlanned || variance.underPlanned ? "card_watch" : "card_watch";
  }

  if (!isBalanceAccount(accountType) || availableBalance == null) {
    return "unknown";
  }

  const projection = calculateBalanceProjection({
    availableBalance,
    plannedDailySpend,
    reserveDailySpend,
    reserveDays,
  });

  if (toNumber(availableBalance) <= 0) {
    return "critical";
  }

  if (projection.operationalBalance != null && projection.operationalBalance <= 0) {
    return "reserve";
  }

  if (
    projection.daysUntilReserve != null &&
    projection.daysUntilReserve <= Math.max(0, alertBeforeReserveDays)
  ) {
    return "request_balance";
  }

  if (variance.overPlanned || variance.underPlanned) {
    return "watch";
  }

  return "ok";
}

export function enrichAccount(account, latestSnapshot = {}) {
  const accountType = account?.accountType ?? account?.account_type ?? "unknown";
  const availableBalance =
    latestSnapshot?.availableBalance ??
    latestSnapshot?.available_balance ??
    latestSnapshot?.estimatedBalance ??
    latestSnapshot?.estimated_balance ??
    null;
  const plannedDailySpend = toNumber(account?.plannedDailySpend ?? account?.planned_daily_spend);
  const reserveDailySpend = toNumber(account?.reserveDailySpend ?? account?.reserve_daily_spend);
  const reserveDays = toNumber(account?.reserveDays ?? account?.reserve_days);
  const projection = calculateBalanceProjection({
    availableBalance,
    plannedDailySpend,
    reserveDailySpend,
    reserveDays,
  });
  const variance = calculateSpendVariance({
    spendToday: latestSnapshot?.spendToday ?? latestSnapshot?.spend_today ?? 0,
    plannedDailySpend,
  });
  const status = classifyAccount(account, latestSnapshot);

  return {
    ...account,
    accountType,
    status,
    statusLabel: STATUS_LABELS[status] ?? status,
    availableBalance,
    reserveAmount: projection.reserveAmount,
    operationalBalance: projection.operationalBalance,
    daysUntilReserve: projection.daysUntilReserve,
    spendRatio: variance.ratio,
    overPlanned: variance.overPlanned,
    underPlanned: variance.underPlanned,
    latestSnapshot,
  };
}

export function sanitizeCredential(credential = {}) {
  const {
    secret,
    token,
    access_token,
    refresh_token,
    developer_token,
    client_secret,
    encrypted_secret,
    decrypted_secret,
    ...safe
  } = credential;

  return {
    ...safe,
    hasSecret: Boolean(
      credential.hasSecret ??
        credential.has_secret ??
        credential.secret_ref ??
        credential.secret_storage ??
        credential.encrypted_secret
    ),
  };
}

export function groupDashboardAccounts(accounts = []) {
  const groups = Object.fromEntries(STATUS_ORDER.map((status) => [status, []]));
  for (const account of accounts) {
    const status = groups[account.status] ? account.status : "unknown";
    groups[status].push(account);
  }
  return groups;
}
